import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, ChevronsUpDown, CreditCard, FileText, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { TR_ADDRESS_DATA } from "@/data/trAddressData";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

function formatTry(amount) {
  return `₺${Number(amount || 0).toLocaleString("tr-TR")}`;
}
function formatTryRight(amount) {
  return `${Number(amount || 0).toLocaleString("tr-TR")} ₺`;
}
function formatDateTime(value) {
  if (!value) return "-";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleString("tr-TR");
}

function parseMoneyLike(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const raw = String(value ?? "").trim();
  if (!raw) return 0;
  const cleaned = raw.replace(/[^\d.,-]/g, "");
  if (!cleaned) return 0;
  // TR format: 6.000,50 -> 6000.50
  if (cleaned.includes(",") && cleaned.includes(".")) {
    return Number(cleaned.replace(/\./g, "").replace(",", ".")) || 0;
  }
  // 6000,50 -> 6000.50
  if (cleaned.includes(",") && !cleaned.includes(".")) {
    return Number(cleaned.replace(",", ".")) || 0;
  }
  return Number(cleaned) || 0;
}

function formatCardForDisplay(raw) {
  const digits = String(raw || "").replace(/[^\d]/g, "").slice(0, 16);
  return digits.replace(/(.{4})/g, "$1 ").trim();
}

function detectCardBrand(raw) {
  const digits = String(raw || "").replace(/[^\d]/g, "");
  if (/^4/.test(digits)) return "VISA";
  if (/^(5[1-5]|2[2-7])/.test(digits)) return "MASTERCARD";
  return "KART";
}

function formatTrPhoneInput(raw) {
  const d = String(raw || "").replace(/[^\d]/g, "").slice(0, 10);
  const p1 = d.slice(0, 3);
  const p2 = d.slice(3, 6);
  const p3 = d.slice(6, 8);
  const p4 = d.slice(8, 10);
  return [p1, p2, p3, p4].filter(Boolean).join(" ");
}

function normalizeExpiryMonth(raw) {
  const digits = String(raw || "").replace(/[^\d]/g, "").slice(0, 2);
  if (!digits) return "";
  if (digits.length === 1) {
    if (Number(digits) > 1) return `0${digits}`;
    return digits;
  }
  const num = Number(digits);
  if (!Number.isFinite(num) || num <= 0) return "01";
  if (num > 12) return "12";
  return String(num).padStart(2, "0");
}

function formatExpiryDisplay(month, year) {
  const m = String(month || "").replace(/[^\d]/g, "").slice(0, 2);
  const y = String(year || "").replace(/[^\d]/g, "").slice(0, 2);
  if (!m && !y) return "";
  return `${m}${y ? ` / ${y}` : ""}`;
}

function getPlanCyclePrice(plan, cycle, fallback = 0) {
  if (!plan) return parseMoneyLike(fallback || 0);
  const keyMap = cycle === "yearly"
    ? ["yearly_price", "annual_price", "price_yearly", "yearly", "price_yillik", "yillik_fiyat", "yearlyAmount"]
    : ["monthly_price", "price", "price_monthly", "monthly", "price_aylik", "aylik_fiyat", "monthlyAmount"];
  for (const k of keyMap) {
    const v = plan[k];
    if (v !== undefined && v !== null && v !== "") return parseMoneyLike(v);
  }
  return parseMoneyLike(fallback || 0);
}

function getIntegrationLimitLabel(plan) {
  const t = String(plan?.name || plan?.key || "").toLowerCase();
  if (t.includes("starter")) return "1 entegrasyon";
  if (t.includes("pro")) return "3 entegrasyon";
  if (t.includes("enterprise")) return "Sınırsız entegrasyon";
  return "Entegrasyon paketi";
}

function getProviderSummary(provider, plan) {
  const base = String(provider || "").trim() || "Entegrasyon";
  const t = String(plan?.name || plan?.key || "").toLowerCase();
  if (t.includes("pro")) return `${base} + 2 entegrasyon`;
  if (t.includes("enterprise")) return `${base} + Sınırsız entegrasyon`;
  return base;
}

const INITIAL_FORM = {
  city_code: "",
  invoice_type: "company",
  company_title: "",
  tax_number: "",
  tax_office: "",
  city: "",
  district: "",
  neighborhood: "",
  address_detail: "",
  address: "",
  user_name: "",
  user_phone: "",
  cc_owner: "",
  card_number: "",
  expiry_month: "",
  expiry_year: "",
  cvv: "",
  promo_code: "",
  terms_approved: false,
};

function SearchableSelect({ value, onChange, options, placeholder, disabled = false, error = false }) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={`h-11 w-full rounded-lg border px-3 text-left text-sm ${error ? "border-rose-400" : "border-border"} bg-card disabled:cursor-not-allowed disabled:bg-muted/50`}
        >
          <div className="flex items-center justify-between gap-2">
            <span className={selected ? "text-foreground" : "text-muted-foreground"}>{selected?.label || placeholder}</span>
            <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder={`${placeholder} ara...`} />
          <CommandList>
            <CommandEmpty>Sonuç bulunamadı.</CommandEmpty>
            {options.map((opt) => (
              <CommandItem
                key={opt.value}
                value={`${opt.label} ${opt.value}`}
                onSelect={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                <Check className={`mr-2 h-4 w-4 ${value === opt.value ? "opacity-100" : "opacity-0"}`} />
                {opt.label}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function BillingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search || ""), [location.search]);
  const viewMode = String(searchParams.get("view") || "").toLowerCase();
  const isPaymentView = viewMode === "payment";
  const isInvoiceDetailView = viewMode === "invoice-detail";
  const intentIdFromQuery = String(searchParams.get("intent") || "");
  const detailStatusFromQuery = String(searchParams.get("status") || "").toLowerCase();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [intents, setIntents] = useState([]);
  const [paidIntents, setPaidIntents] = useState([]);
  const [selectedIntentId, setSelectedIntentId] = useState("");
  const [packageOptions, setPackageOptions] = useState([]);
  const [packageMeta, setPackageMeta] = useState({ title: "", subtitle: "", trial_days: null });
  const [planMap, setPlanMap] = useState({});
  const [trial, setTrial] = useState({ is_trial: false, days_left: null });
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [cardFace, setCardFace] = useState("front");
  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [billingCycleUI, setBillingCycleUI] = useState("monthly");
  const [selectedPlanKeyUI, setSelectedPlanKeyUI] = useState("");
  const [taxLookupLoading, setTaxLookupLoading] = useState(false);
  const [saveCardForRenewal, setSaveCardForRenewal] = useState(false);
  const [paytrIframeSrc, setPaytrIframeSrc] = useState("");

  const selectedIntent = useMemo(
    () => {
      if (!selectedIntentId) return null;
      return intents.find((x) => x.id === selectedIntentId) || null;
    },
    [intents, selectedIntentId]
  );

  const activePlanKey = useMemo(
    () => String(selectedPlanKeyUI || selectedIntent?.plan_key || "").toLowerCase(),
    [selectedPlanKeyUI, selectedIntent?.plan_key]
  );

  const selectedPlan = useMemo(() => {
    if (!activePlanKey) return null;
    return planMap[activePlanKey] || null;
  }, [planMap, activePlanKey]);

  const packageCards = useMemo(() => {
    const list = (packageOptions?.length ? packageOptions : Object.values(planMap)).filter(Boolean);
    const priority = (p) => {
      const t = String(p?.name || p?.key || "").toLowerCase();
      if (t.includes("starter")) return 1;
      if (t.includes("pro")) return 2;
      if (t.includes("enterprise")) return 3;
      return 99;
    };
    return [...list].sort((a, b) => priority(a) - priority(b));
  }, [packageOptions, planMap]);
  const selectedCity = useMemo(() => {
    if (form.city_code) return TR_ADDRESS_DATA.find((c) => c.code === form.city_code) || null;
    if (form.city) return TR_ADDRESS_DATA.find((c) => c.name === form.city) || null;
    return null;
  }, [form.city_code, form.city]);
  const districts = useMemo(
    () => (selectedCity?.districts || []).map((d) => d.name),
    [selectedCity]
  );
  const neighborhoods = useMemo(() => {
    if (!selectedCity || !form.district) return [];
    return selectedCity.districts.find((d) => d.name === form.district)?.neighborhoods || [];
  }, [selectedCity, form.district]);
  const cityOptions = useMemo(
    () => TR_ADDRESS_DATA.map((city) => ({ value: city.code, label: city.name })),
    []
  );
  const districtOptions = useMemo(
    () => districts.map((d) => ({ value: d, label: d })),
    [districts]
  );
  const neighborhoodOptions = useMemo(
    () => neighborhoods.map((n) => ({ value: n, label: n })),
    [neighborhoods]
  );

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const normalizePhone = (raw) => String(raw || "").replace(/[^\d+]/g, "");
  const normalizeCardNo = (raw) => String(raw || "").replace(/[^\d]/g, "").slice(0, 16);

  const loadData = async ({ preferredPlanKey = "", preferredCycle = "", autoSelectFirst = false } = {}) => {
    setLoading(true);
    try {
      const [packagesRes, intentRes, paidRes, trialRes] = await Promise.all([
        api.get("/billing/packages").catch(() => null),
        api.get("/billing/checkout-intents", { params: { status: "pending_payment" } }),
        api.get("/billing/checkout-intents", { params: { status: "paid" } }).catch(() => ({ items: [] })),
        api.get("/billing/trial-status").catch(() => null),
      ]);
      const plans = {};
      const packageList = packagesRes?.packages || [];
      setPackageMeta({
        title: String(packagesRes?.title || ""),
        subtitle: String(packagesRes?.subtitle || ""),
        trial_days: typeof packagesRes?.trial_days === "number" ? packagesRes.trial_days : null,
      });
      for (const p of packageList) plans[String(p.key || "").toLowerCase()] = p;
      setPackageOptions(packageList);
      setPlanMap(plans);
      const rows = Array.isArray(intentRes?.items) ? intentRes.items : [];
      const paidRows = Array.isArray(paidRes?.items) ? paidRes.items : [];
      setIntents(rows);
      setPaidIntents(paidRows);
      setSelectedIntentId((prev) => {
        if (!rows.length) return "";
        if (preferredPlanKey) {
          const byPlanAndCycle = rows.find((r) => (
            String(r.plan_key || "").toLowerCase() === String(preferredPlanKey || "").toLowerCase()
            && (!preferredCycle || String(r.billing_cycle || "") === String(preferredCycle || ""))
          ));
          if (byPlanAndCycle) return byPlanAndCycle.id;
          const byPlan = rows.find((r) => String(r.plan_key || "").toLowerCase() === String(preferredPlanKey || "").toLowerCase());
          if (byPlan) return byPlan.id;
        }
        if (prev && rows.some((r) => r.id === prev)) return prev;
        return autoSelectFirst ? rows[0].id : "";
      });
      setTrial({
        is_trial: Boolean(trialRes?.is_trial),
        days_left: typeof trialRes?.days_left === "number" ? trialRes.days_left : null,
      });
    } catch (err) {
      toast.error(err.message || "Faturalandırma verileri yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const ensureIntentFromQuery = async () => {
    const params = new URLSearchParams(location.search || "");
    const source = params.get("source") || "";
    const provider = params.get("provider") || "";
    const kind = params.get("kind") || "";
    const plan = params.get("plan") || "";
    const cycle = params.get("cycle") || "monthly";
    const next = params.get("next") || "/panel/entegrasyonlar";
    if (source !== "integration" || !plan) return;
    await api.post("/billing/checkout-intent", {
      plan_key: plan,
      billing_cycle: cycle,
      source,
      provider,
      integration_kind: kind,
      redirect_to: next,
    });
  };

  useEffect(() => {
    (async () => {
      try {
        await ensureIntentFromQuery();
      } catch (err) {
        toast.error(err?.message || "Ödeme kaydı oluşturulamadı");
      } finally {
        await loadData({ autoSelectFirst: false });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const changeBillingCycle = async (cycle) => {
    if (!activePlanKey || !selectedIntent) return;
    const prevCycle = billingCycleUI;
    const nextAmount = getPlanCyclePrice(selectedPlan, cycle, selectedIntent?.amount || 0);
    setBillingCycleUI(cycle);
    setIntents((prev) => prev.map((x) => (
      x.id === selectedIntent.id
        ? { ...x, billing_cycle: cycle, amount: nextAmount, updated_at: new Date().toISOString() }
        : x
    )));
    try {
      const params = new URLSearchParams(location.search || "");
      await api.post("/billing/checkout-intent", {
        plan_key: activePlanKey,
        billing_cycle: cycle,
        source: selectedIntent?.source || params.get("source") || "integration",
        provider: selectedIntent?.provider || params.get("provider") || "",
        integration_kind: selectedIntent?.integration_kind || params.get("kind") || "",
        redirect_to: selectedIntent?.redirect_to || params.get("next") || "/panel/entegrasyonlar",
      });
      await loadData({ preferredPlanKey: activePlanKey, preferredCycle: cycle });
      toast.success(cycle === "yearly" ? "Yıllık abonelik seçildi" : "Aylık abonelik seçildi");
    } catch (err) {
      setBillingCycleUI(prevCycle);
      setIntents((prev) => prev.map((x) => (
        x.id === selectedIntent.id
          ? { ...x, billing_cycle: prevCycle }
          : x
      )));
      toast.error(err?.message || "Abonelik tercihi güncellenemedi");
    }
  };

  const changePlan = async (planKey) => {
    if (!selectedIntent) {
      toast.error("Önce ödeme kaydı seçin.");
      return;
    }
    const normalizedPlanKey = String(planKey || "").toLowerCase();
    if (!normalizedPlanKey || normalizedPlanKey === activePlanKey) return;
    const prevPlanKey = selectedPlanKeyUI;
    setSelectedPlanKeyUI(normalizedPlanKey);
    try {
      const params = new URLSearchParams(location.search || "");
      await api.post("/billing/checkout-intent", {
        plan_key: normalizedPlanKey,
        billing_cycle: billingCycleUI,
        source: selectedIntent?.source || params.get("source") || "integration",
        provider: selectedIntent?.provider || params.get("provider") || "",
        integration_kind: selectedIntent?.integration_kind || params.get("kind") || "",
        redirect_to: selectedIntent?.redirect_to || params.get("next") || "/panel/entegrasyonlar",
      });
      await loadData({ preferredPlanKey: normalizedPlanKey, preferredCycle: billingCycleUI });
      toast.success("Paket seçimi güncellendi");
    } catch (err) {
      setSelectedPlanKeyUI(prevPlanKey);
      toast.error(err?.message || "Paket seçimi güncellenemedi");
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!selectedIntent) nextErrors.intent = "Ödeme bekleyen bir kayıt bulunamadı.";
    if (!form.user_name.trim()) nextErrors.user_name = "Ad Soyad zorunlu.";
    if (!form.user_phone.trim()) nextErrors.user_phone = "Telefon zorunlu.";
    if (String(form.user_phone || "").replace(/[^\d]/g, "").length !== 10) nextErrors.user_phone = "Telefon 10 haneli olmalı.";
    if (!form.city.trim()) nextErrors.city = "Şehir zorunlu.";
    if (!form.district.trim()) nextErrors.district = "İlçe zorunlu.";
    if (!form.neighborhood.trim()) nextErrors.neighborhood = "Mahalle zorunlu.";
    if (!form.address_detail.trim()) nextErrors.address_detail = "Adres devamı zorunlu.";
    if (!form.address.trim()) nextErrors.address = "Adres zorunlu.";
    if (form.invoice_type === "company" && !form.company_title.trim()) nextErrors.company_title = "Firma ünvanı zorunlu.";
    if (!form.tax_number.trim()) nextErrors.tax_number = form.invoice_type === "company" ? "Vergi numarası zorunlu." : "TC Kimlik No zorunlu.";
    if (form.invoice_type === "personal" && String(form.tax_number || "").replace(/[^\d]/g, "").length !== 11) {
      nextErrors.tax_number = "TC Kimlik No 11 haneli olmalı.";
    }
    if (!form.tax_office.trim()) nextErrors.tax_office = "Vergi dairesi zorunlu.";
    if (!form.terms_approved) nextErrors.terms_approved = "Devam etmek için sözleşmeyi onaylayın.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const continuePayment = async () => {
    if (!validate()) return;
    if (!selectedIntent) return;
    setSaving(true);
    try {
      const fullAddress = [form.neighborhood, form.address_detail, form.district, form.city].filter(Boolean).join(" ");
      const init = await api.post(`/billing/checkout-intents/${selectedIntent.id}/paytr-init`, {
        user_name: form.user_name.trim(),
        user_address: fullAddress || form.address.trim(),
        user_phone: normalizePhone(`+90${form.user_phone}`),
        installment_count: 0,
        currency: "TL",
        non_3d: 0,
        client_lang: "tr",
        save_card: Boolean(saveCardForRenewal),
        card_brand: detectCardBrand(form.card_number),
        card_last4: String(normalizeCardNo(form.card_number)).slice(-4),
      });
      const fields = init?.fields || {};
      const postUrl = init?.post_url || "https://www.paytr.com/odeme";
      if (init?.mode === "iframe" && init?.iframe_src) {
        setPaytrIframeSrc(String(init.iframe_src));
        return;
      }
      const submit = document.createElement("form");
      submit.method = "POST";
      submit.action = postUrl;
      submit.style.display = "none";
      Object.entries(fields).forEach(([k, v]) => {
        const i = document.createElement("input");
        i.type = "hidden";
        i.name = k;
        i.value = String(v ?? "");
        submit.appendChild(i);
      });
      const cardFields = {
        cc_owner: form.cc_owner.trim(),
        card_number: normalizeCardNo(form.card_number),
        expiry_month: form.expiry_month.trim(),
        expiry_year: form.expiry_year.trim(),
        cvv: form.cvv.trim(),
      };
      Object.entries(cardFields).forEach(([k, v]) => {
        const i = document.createElement("input");
        i.type = "hidden";
        i.name = k;
        i.value = String(v ?? "");
        submit.appendChild(i);
      });
      document.body.appendChild(submit);
      submit.submit();
    } catch (err) {
      toast.error(err?.message || "PayTR başlatılamadı");
    } finally {
      setSaving(false);
    }
  };

  const lookupTaxInfo = async () => {
    const taxNo = String(form.tax_number || "").replace(/[^\d]/g, "");
    if (!taxNo) return;
    setTaxLookupLoading(true);
    try {
      const res = await api.post("/billing/tax-lookup", { tax_number: taxNo });
      if (res?.company_title) setField("company_title", res.company_title);
      if (res?.tax_office) setField("tax_office", res.tax_office);
      toast.success("Firma bilgileri bulundu ve dolduruldu.");
    } catch (err) {
      toast.error(err?.message || "Vergi numarası ile firma bilgisi bulunamadı.");
    } finally {
      setTaxLookupLoading(false);
    }
  };

  useEffect(() => {
    setAppliedPromo(null);
    setDiscountAmount(0);
  }, [selectedIntentId, selectedIntent?.amount]);

  useEffect(() => {
    const cycle = selectedIntent?.billing_cycle;
    if (!cycle) return;
    setBillingCycleUI(cycle === "yearly" ? "yearly" : "monthly");
  }, [selectedIntent?.id, selectedIntent?.billing_cycle]);

  useEffect(() => {
    if (!selectedIntent?.plan_key) return;
    setSelectedPlanKeyUI(String(selectedIntent.plan_key || "").toLowerCase());
  }, [selectedIntent?.id, selectedIntent?.plan_key]);

  useEffect(() => {
    if (!isPaymentView) return;
    if (intentIdFromQuery && intents.some((x) => x.id === intentIdFromQuery)) {
      setSelectedIntentId(intentIdFromQuery);
      return;
    }
    if (!selectedIntentId && intents.length > 0) {
      setSelectedIntentId(intents[0].id);
    }
  }, [isPaymentView, intentIdFromQuery, intents, selectedIntentId]);

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">Faturalandırma yükleniyor...</div>;
  }

  const selectedIsYearly = billingCycleUI === "yearly";
  const selectedPeriodLabel = selectedIsYearly ? "Yıllık Abonelik" : "Aylık Abonelik";
  const providerSummary = getProviderSummary(selectedIntent?.provider, selectedPlan);
  const cardBrand = detectCardBrand(form.card_number);
  const monthlyBase = getPlanCyclePrice(selectedPlan, "monthly", selectedIntent?.amount || 0);
  const yearlyBase = getPlanCyclePrice(selectedPlan, "yearly", (selectedIntent?.amount || 0) * 12);
  const yearlyAdvantage = Math.max(0, (monthlyBase * 12) - yearlyBase);
  const baseAmount = Number(getPlanCyclePrice(selectedPlan, selectedIsYearly ? "yearly" : "monthly", selectedIntent?.amount || 0));
  const totalAmount = Math.max(0, baseAmount - discountAmount);

  const applyPromo = () => {
    if (!selectedIntent) return;
    const code = String(promoInput || "").trim().toUpperCase();
    if (!code) return;
    const rules = {
      HOSGELDIN15: 0.15,
      PRO10: 0.1,
      YENGECSPECIAL: 0.2,
    };
    const rate = rules[code];
    if (!rate) {
      setAppliedPromo(null);
      setDiscountAmount(0);
      toast.error("Promosyon kodu geçersiz");
      return;
    }
    const discount = Math.round(baseAmount * rate);
    setAppliedPromo(code);
    setDiscountAmount(discount);
    toast.success(`Promosyon uygulandı: -${formatTry(discount)}`);
  };

  const openPaymentView = (intentId) => {
    const params = new URLSearchParams(location.search || "");
    params.set("view", "payment");
    params.set("intent", intentId);
    navigate(`/panel/hesabim/faturalandirma?${params.toString()}`);
  };

  const openDetailView = (intentId, status) => {
    const params = new URLSearchParams(location.search || "");
    params.set("view", "invoice-detail");
    params.set("intent", intentId);
    params.set("status", status === "paid" ? "paid" : "pending");
    navigate(`/panel/hesabim/faturalandirma?${params.toString()}`);
  };

  const triggerFileDownload = (filename, content) => {
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleInvoiceDownload = (row, type = "invoice") => {
    const filename = type === "refund" ? `iade-faturasi-${row.id}.txt` : `fatura-${row.id}.txt`;
    const title = type === "refund" ? "İade Faturası" : "Fatura";
    const planName = planMap[String(row.plan_key || "").toLowerCase()]?.name || row.plan_key || "Paket";
    const body = [
      `${title}`,
      `Kayıt No: ${row.id}`,
      `Paket: ${planName}`,
      `Durum: ${row._status === "paid" ? "Fatura Hazır" : "Ödeme Bekleniyor"}`,
      `Tutar: ${formatTryRight(row.amount || 0)}`,
      `Tarih: ${formatDateTime(row.paid_at || row.updated_at || row.created_at)}`,
      `Sağlayıcı: ${row.provider || "-"}`,
    ].join("\n");
    triggerFileDownload(filename, body);
    toast.success(`${title} indirildi`);
  };

  const openListView = () => {
    const params = new URLSearchParams(location.search || "");
    params.delete("view");
    params.delete("intent");
    const q = params.toString();
    navigate(q ? `/panel/hesabim/faturalandirma?${q}` : "/panel/hesabim/faturalandirma");
  };

  const statusPillClass = (isPending) => (
    `inline-flex rounded-lg px-2.5 py-1 text-xs font-semibold ${
      isPending ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200" : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
    }`
  );

  if (!isPaymentView && !isInvoiceDetailView) {
    const uiRows = [...paidIntents]
      .map((x) => ({ ...x, _status: "paid" }))
      .sort((a, b) => String(b.updated_at || b.created_at || "").localeCompare(String(a.updated_at || a.created_at || "")));

    return (
      <div className="page-shell space-y-5">
        <div className="page-header mb-2">
          <div className="app-page-header">
            <h1 className="app-page-title">Fatura Merkezi</h1>
            <p className="app-page-subtitle">Ödeme geçmişinizi ve fatura kayıtlarınızı görüntüleyin.</p>
          </div>
        </div>
        <div className="space-y-4">
          <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            <div className="space-y-3 p-3">
              {uiRows.length === 0 ? (
                <div className="px-4 py-10 text-sm text-muted-foreground">Henüz tamamlanmış fatura kaydı bulunmuyor.</div>
              ) : uiRows.map((row) => {
                const plan = planMap[String(row.plan_key || "").toLowerCase()];
                return (
                  <div key={`${row.id}-${row._status}`} className="rounded-2xl border border-border bg-card px-4 py-4 shadow-sm">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-[1.2fr_0.9fr_0.9fr] md:items-center">
                      <div>
                        <div className="text-sm font-semibold text-foreground">
                          {plan?.name || row.plan_key || "Paket"}
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {getProviderSummary(row.provider, plan)} · {String(row.billing_cycle || "monthly") === "yearly" ? "Yıllık" : "Aylık"}
                        </div>
                        <div className="mt-1 text-[11px] text-[#8ca2bf]">Kayıt No: {row.id}</div>
                      </div>
                      <div>
                        <span className={statusPillClass(false)}>
                          Fatura Hazır
                        </span>
                        <div className="mt-2 text-xs text-muted-foreground">{formatDateTime(row.paid_at || row.updated_at || row.created_at)}</div>
                      </div>
                      <div className="text-left md:text-right">
                        <div className="text-[11px] text-muted-foreground">Tutar</div>
                        <div className="mt-1 text-base font-semibold text-foreground">{formatTryRight(row.amount || 0)}</div>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => openDetailView(row.id, row._status)}
                        className="h-9 rounded-xl border border-border bg-card px-3 text-xs font-semibold text-foreground hover:bg-muted/30"
                      >
                        Detay
                      </button>
                      <button
                        type="button"
                        onClick={() => handleInvoiceDownload(row, "invoice")}
                        className="h-9 rounded-xl border border-border bg-card px-3 text-xs font-semibold text-foreground hover:bg-muted/30"
                      >
                        Faturayı İndir
                      </button>
                      {Boolean(row.refund_invoice_id || row.refund_invoice_url) ? (
                        <button
                          type="button"
                          onClick={() => handleInvoiceDownload(row, "refund")}
                          className="h-9 rounded-xl border border-rose-200 bg-rose-50 px-3 text-xs font-semibold text-rose-700 hover:bg-rose-100"
                        >
                          İade Faturası
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </div>
    );
  }

  if (isInvoiceDetailView) {
    const detailSource = detailStatusFromQuery === "paid" ? paidIntents : intents;
    const detailRow = detailSource.find((x) => x.id === intentIdFromQuery) || null;
    const detailPlan = planMap[String(detailRow?.plan_key || "").toLowerCase()];
    const detailIsPaid = detailStatusFromQuery === "paid";
    return (
      <div className="page-shell space-y-5">
        <div className="page-header mb-2">
          <div className="app-page-header">
            <h1 className="app-page-title">Fatura Detayı</h1>
          </div>
          <div className="page-header-actions">
            <button type="button" onClick={openListView} className="h-9 rounded-lg border border-border bg-card px-3 text-xs font-semibold text-foreground hover:bg-muted/60 transition-colors">← Listeye Dön</button>
          </div>
        </div>
        <div className="space-y-4">
          <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            <div className="space-y-3 p-5">
              {!detailRow ? (
                <p className="text-sm text-muted-foreground">Fatura detayı bulunamadı.</p>
              ) : (
                <>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded-xl border border-border bg-muted/30 p-3"><div className="text-xs text-muted-foreground">Paket</div><div className="mt-1 text-sm font-semibold text-foreground">{detailPlan?.name || detailRow.plan_key || "-"}</div></div>
                    <div className="rounded-xl border border-border bg-muted/30 p-3"><div className="text-xs text-muted-foreground">Durum</div><div className="mt-1"><span className={statusPillClass(!detailIsPaid)}>{detailIsPaid ? "Fatura Hazır" : "Ödeme Bekleniyor"}</span></div></div>
                    <div className="rounded-xl border border-border bg-muted/30 p-3"><div className="text-xs text-muted-foreground">Tarih</div><div className="mt-1 text-sm font-semibold text-foreground">{formatDateTime(detailRow.paid_at || detailRow.updated_at || detailRow.created_at)}</div></div>
                    <div className="rounded-xl border border-border bg-muted/30 p-3"><div className="text-xs text-muted-foreground">Tutar</div><div className="mt-1 text-sm font-semibold text-foreground">{formatTryRight(detailRow.amount || 0)}</div></div>
                  </div>
                  {!detailIsPaid ? (
                    <div className="flex justify-end">
                      <button type="button" onClick={() => openPaymentView(detailRow.id)} className="h-10 rounded-xl bg-[#1487a3] hover:bg-[#12779b] px-5 text-xs font-semibold text-white hover:opacity-95">Ödemeyi Tamamla</button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => handleInvoiceDownload(detailRow, "invoice")}
                        className="h-9 rounded-xl border border-border bg-card px-3 text-xs font-semibold text-foreground hover:bg-muted/30"
                      >
                        Faturayı İndir
                      </button>
                      {Boolean(detailRow.refund_invoice_id || detailRow.refund_invoice_url) ? (
                        <button
                          type="button"
                          onClick={() => handleInvoiceDownload(detailRow, "refund")}
                          className="h-9 rounded-xl border border-rose-200 bg-rose-50 px-3 text-xs font-semibold text-rose-700 hover:bg-rose-100"
                        >
                          İade Faturası
                        </button>
                      ) : null}
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell space-y-5">
      {paytrIframeSrc ? (
        <div className="fixed inset-0 z-[120] bg-slate-950/70 p-3 md:p-8">
          <div className="mx-auto flex h-full w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h3 className="text-sm font-semibold text-foreground">PayTR Güvenli Ödeme</h3>
              <button
                type="button"
                onClick={() => setPaytrIframeSrc("")}
                className="rounded-lg border border-border bg-card px-3 py-1 text-xs font-semibold text-foreground/80 hover:bg-muted/50"
              >
                Kapat
              </button>
            </div>
            <iframe
              title="paytr-iframe"
              src={paytrIframeSrc}
              className="h-full w-full border-0"
              allow="payment"
            />
          </div>
        </div>
      ) : null}
      <div className="space-y-4">
        <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
          <div className="bg-muted/40 border-b border-border px-6 py-4 md:px-8">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Güvenli Ödeme Merkezi</p>
                <h1 className="mt-1 app-page-title text-foreground">Ödeme Ekranı</h1>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={openListView}
                  className="rounded-full border border-white/35 bg-white/10 px-3 py-1 text-xs font-semibold text-white hover:bg-white/20"
                >
                  Faturalar Listesi
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-4 lg:grid-cols-[1.3fr_0.7fr]">
          <div className="order-2 space-y-5 lg:order-1">
            {!selectedIntent ? (
              <article className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <p className="text-sm text-muted-foreground">Lütfen yukarıdaki listeden bir paket kaydı seçin. Seçim yaptığınızda fatura ve ödeme alanları açılır.</p>
              </article>
            ) : null}
            {selectedIntent ? (
              <>
            <article className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <FileText className="h-4 w-4 text-[#2f79c7]" />
                <h2 className="text-base font-semibold text-[#163f76]">Fatura Bilgileri</h2>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="md:col-span-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-[#5e7ea4]">Fatura Tipi</label>
                    <div className="inline-flex w-full rounded-2xl border border-[#d7e5f7] bg-[linear-gradient(180deg,#f8fbff_0%,#eef5ff_100%)] p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_8px_22px_rgba(27,74,136,0.08)]">
                      <button
                        type="button"
                        onClick={() => setField("invoice_type", "company")}
                        className={`h-10 flex-1 rounded-xl text-xs font-semibold transition-all duration-200 ${form.invoice_type === "company" ? "border border-[#cddff7] bg-[linear-gradient(180deg,#ffffff_0%,#f2f7ff_100%)] text-[#194f99] shadow-[0_8px_20px_rgba(31,95,182,0.2)]" : "text-muted-foreground hover:bg-white/70"}`}
                      >
                        Limited / Anonim
                      </button>
                      <button
                        type="button"
                        onClick={() => setField("invoice_type", "personal")}
                        className={`h-10 flex-1 rounded-xl text-xs font-semibold transition-all duration-200 ${form.invoice_type === "personal" ? "border border-[#cddff7] bg-[linear-gradient(180deg,#ffffff_0%,#f2f7ff_100%)] text-[#194f99] shadow-[0_8px_20px_rgba(31,95,182,0.2)]" : "text-muted-foreground hover:bg-white/70"}`}
                      >
                        Şahıs Şirketi
                      </button>
                    </div>
                  </div>
                </div>
                <div className="md:col-span-2 grid gap-3 md:grid-cols-2">
                  <div>
                    <div className="flex gap-2">
                      <div className="relative w-full">
                        <input
                          placeholder=" "
                          className={`peer h-11 w-full rounded-lg border px-3 pt-4 text-sm ${errors.tax_number ? "border-rose-400" : "border-border"}`}
                          value={form.tax_number}
                          onChange={(e) => setField("tax_number", String(e.target.value || "").replace(/[^\d]/g, "").slice(0, 11))}
                        />
                        <label className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-sm peer-placeholder-shown:text-[#9bb0c9] peer-focus:top-3 peer-focus:text-xs peer-focus:text-[#1f5fb6] peer-[:not(:placeholder-shown)]:top-3 peer-[:not(:placeholder-shown)]:text-xs">
                          {form.invoice_type === "company" ? "Vergi Numarası" : "TC Kimlik Numarası"}
                        </label>
                      </div>
                      {form.invoice_type === "company" ? (
                        <button
                          type="button"
                          onClick={lookupTaxInfo}
                          disabled={taxLookupLoading || String(form.tax_number || "").replace(/[^\d]/g, "").length < 10}
                          className="h-11 shrink-0 rounded-lg border border-[#b9d3f3] bg-[#f3f8ff] px-3 text-xs font-semibold text-[#1f5fb6] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {taxLookupLoading ? "Sorgulanıyor..." : "VKN Sorgula"}
                        </button>
                      ) : null}
                    </div>
                    {errors.tax_number ? <p className="mt-1 text-[11px] text-rose-500">{errors.tax_number}</p> : null}
                  </div>
                  <div>
                    <div className="relative">
                      <input
                        placeholder=" "
                        className={`peer h-11 w-full rounded-lg border px-3 pt-4 text-sm ${errors.tax_office ? "border-rose-400" : "border-border"}`}
                        value={form.tax_office}
                        onChange={(e) => setField("tax_office", e.target.value)}
                      />
                      <label className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-sm peer-placeholder-shown:text-[#9bb0c9] peer-focus:top-3 peer-focus:text-xs peer-focus:text-[#1f5fb6] peer-[:not(:placeholder-shown)]:top-3 peer-[:not(:placeholder-shown)]:text-xs">
                        Vergi Dairesi
                      </label>
                    </div>
                    {errors.tax_office ? <p className="mt-1 text-[11px] text-rose-500">{errors.tax_office}</p> : null}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <div className="relative">
                    <input
                      disabled={form.invoice_type !== "company"}
                      placeholder=" "
                      className={`peer h-12 w-full rounded-lg border px-3 pt-4 text-sm disabled:bg-muted/50 ${errors.company_title ? "border-rose-400" : "border-border"}`}
                      value={form.company_title}
                      onChange={(e) => setField("company_title", e.target.value)}
                    />
                    <label className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-sm peer-placeholder-shown:text-[#9bb0c9] peer-focus:top-3 peer-focus:text-xs peer-focus:text-[#1f5fb6] peer-[:not(:placeholder-shown)]:top-3 peer-[:not(:placeholder-shown)]:text-xs">
                      Firma Ünvanı
                    </label>
                  </div>
                  {errors.company_title ? <p className="mt-1 text-[11px] text-rose-500">{errors.company_title}</p> : null}
                </div>

                <div className="md:col-span-2 grid gap-3 md:grid-cols-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-[#5e7ea4]">Şehir</label>
                    <SearchableSelect
                      value={form.city_code}
                      placeholder="Şehir seçin"
                      options={cityOptions}
                      error={Boolean(errors.city)}
                      onChange={(value) => {
                        const selected = TR_ADDRESS_DATA.find((c) => c.code === value);
                        setField("city_code", value);
                        setField("city", selected?.name || "");
                        setField("district", "");
                        setField("neighborhood", "");
                      }}
                    />
                    {errors.city ? <p className="mt-1 text-[11px] text-rose-500">{errors.city}</p> : null}
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-[#5e7ea4]">İlçe</label>
                    <SearchableSelect
                      value={form.district}
                      placeholder="İlçe seçin"
                      options={districtOptions}
                      disabled={!form.city}
                      error={Boolean(errors.district)}
                      onChange={(value) => {
                        setField("district", value);
                        setField("neighborhood", "");
                      }}
                    />
                    {errors.district ? <p className="mt-1 text-[11px] text-rose-500">{errors.district}</p> : null}
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-[#5e7ea4]">Mahalle</label>
                    <SearchableSelect
                      value={form.neighborhood}
                      placeholder={neighborhoodOptions.length ? "Mahalle seçin" : "Mahalle yazılacak"}
                      options={neighborhoodOptions}
                      disabled={!form.district || neighborhoodOptions.length === 0}
                      error={Boolean(errors.neighborhood)}
                      onChange={(value) => setField("neighborhood", value)}
                    />
                    {form.district && neighborhoodOptions.length === 0 ? (
                      <div className="relative mt-2">
                        <input
                          className={`peer h-11 w-full rounded-lg border px-3 pt-4 text-sm ${errors.neighborhood ? "border-rose-400" : "border-border"}`}
                          placeholder=" "
                          value={form.neighborhood}
                          onChange={(e) => setField("neighborhood", e.target.value)}
                        />
                        <label className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-sm peer-placeholder-shown:text-[#9bb0c9] peer-focus:top-3 peer-focus:text-xs peer-focus:text-[#1f5fb6] peer-[:not(:placeholder-shown)]:top-3 peer-[:not(:placeholder-shown)]:text-xs">
                          Bu ilçe için mahalleyi yazın
                        </label>
                      </div>
                    ) : null}
                    {errors.neighborhood ? <p className="mt-1 text-[11px] text-rose-500">{errors.neighborhood}</p> : null}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <div className="relative">
                    <textarea
                      className={`peer min-h-[88px] w-full rounded-lg border px-3 pb-3 pt-6 text-sm ${errors.address_detail ? "border-rose-400" : "border-border"}`}
                      placeholder=" "
                      value={form.address_detail}
                      onChange={(e) => {
                        setField("address_detail", e.target.value);
                        const fullAddress = [form.neighborhood, e.target.value, form.district, form.city].filter(Boolean).join(" ");
                        setField("address", fullAddress);
                      }}
                    />
                    <label className="pointer-events-none absolute left-3 top-3 text-xs text-muted-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-sm peer-placeholder-shown:text-[#9bb0c9] peer-focus:top-3 peer-focus:text-xs peer-focus:text-[#1f5fb6] peer-[:not(:placeholder-shown)]:top-3 peer-[:not(:placeholder-shown)]:text-xs">
                      Adres Devamı
                    </label>
                  </div>
                  {errors.address_detail ? <p className="mt-1 text-[11px] text-rose-500">{errors.address_detail}</p> : null}
                </div>

                <div className="md:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-[#5e7ea4]">Adres Özeti (Otomatik)</label>
                  <input
                    readOnly
                    className={`h-11 w-full rounded-lg border px-3 text-sm ${errors.address ? "border-rose-400" : "border-border"} bg-muted/50`}
                    value={form.address}
                  />
                  {errors.address ? <p className="mt-1 text-[11px] text-rose-500">{errors.address}</p> : null}
                </div>
                <div>
                  <div className="relative">
                    <input
                      className={`peer h-11 w-full rounded-lg border px-3 pt-4 text-sm ${errors.user_name ? "border-rose-400" : "border-border"}`}
                      placeholder=" "
                      value={form.user_name}
                      onChange={(e) => setField("user_name", e.target.value)}
                    />
                    <label className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-sm peer-placeholder-shown:text-[#9bb0c9] peer-focus:top-3 peer-focus:text-xs peer-focus:text-[#1f5fb6] peer-[:not(:placeholder-shown)]:top-3 peer-[:not(:placeholder-shown)]:text-xs">
                      Ad Soyad
                    </label>
                  </div>
                  {errors.user_name ? <p className="mt-1 text-[11px] text-rose-500">{errors.user_name}</p> : null}
                </div>
                <div>
                  <div className={`relative flex h-11 overflow-hidden rounded-lg border ${errors.user_phone ? "border-rose-400" : "border-border"}`}>
                    <span className="inline-flex items-center border-r border-border bg-muted/30 px-3 text-sm font-semibold text-[#3a5f8c]">+90</span>
                    <input
                      className="peer w-full px-3 pt-4 text-sm outline-none"
                      placeholder=" "
                      value={formatTrPhoneInput(form.user_phone)}
                      onChange={(e) => setField("user_phone", formatTrPhoneInput(e.target.value))}
                    />
                    <label className="pointer-events-none absolute left-[58px] top-1/2 -translate-y-1/2 text-xs text-muted-foreground transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-sm peer-placeholder-shown:text-[#9bb0c9] peer-focus:top-3 peer-focus:text-xs peer-focus:text-[#1f5fb6] peer-[:not(:placeholder-shown)]:top-3 peer-[:not(:placeholder-shown)]:text-xs">
                      Telefon
                    </label>
                  </div>
                  {errors.user_phone ? <p className="mt-1 text-[11px] text-rose-500">{errors.user_phone}</p> : null}
                </div>
              </div>
            </article>

            <article className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-[#2f79c7]" />
                <h2 className="text-base font-semibold text-[#163f76]">Kart Bilgileri</h2>
              </div>
              <div className="grid items-start gap-4 lg:grid-cols-[0.88fr_1.12fr] lg:items-start">
                <div className="self-start [perspective:1200px]">
                  <div className={`relative h-[156px] w-full rounded-2xl transition-transform duration-500 [transform-style:preserve-3d] ${cardFace === "back" ? "[transform:rotateY(180deg)]" : ""}`}>
                    <div className="absolute inset-0 rounded-2xl bg-[linear-gradient(130deg,#122f67_0%,#1b5ea8_50%,#0f8aa1_100%)] p-4 text-white [backface-visibility:hidden]">
                      <div className="flex items-start justify-between">
                        <div className="h-9 w-11 rounded-md bg-white/20" />
                        <div className="rounded-full border border-white/30 px-2 py-0.5 text-[10px] font-semibold">{cardBrand}</div>
                      </div>
                      <div className="mt-6 w-full whitespace-nowrap text-left text-[22px] leading-none tracking-[0.06em]">{formatCardForDisplay(form.card_number || "0000000000000000") || "0000 0000 0000 0000"}</div>
                      <div className="mt-4 flex items-end justify-between">
                        <div>
                          <div className="text-[9px] uppercase text-blue-100">Kart Sahibi</div>
                          <div className="text-xs font-medium">{form.cc_owner || "AD SOYAD"}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-[9px] uppercase text-blue-100">Son Kul.</div>
                          <div className="text-xs font-medium">{(form.expiry_month || "AA")}/{(form.expiry_year || "YY")}</div>
                        </div>
                      </div>
                    </div>
                    <div className="absolute inset-0 rounded-2xl bg-[linear-gradient(130deg,#0f264f_0%,#18457c_55%,#15748a_100%)] p-4 text-white [backface-visibility:hidden] [transform:rotateY(180deg)]">
                      <div className="mt-2 h-10 w-full bg-black/60" />
                      <div className="mt-5 rounded-md bg-card p-2 text-right text-foreground">
                        <span className="pr-2 text-sm font-semibold tracking-[0.2em]">{form.cvv || "***"}</span>
                      </div>
                      <p className="mt-3 text-[11px] text-blue-100">Güvenlik kodunu yalnızca siz görebilirsiniz.</p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 content-start">
                  <div className="md:col-span-2">
                  <div className="relative">
                    <input
                      onFocus={() => setCardFace("front")}
                      placeholder=" "
                      className={`peer h-11 w-full rounded-lg border px-3 pt-4 text-sm ${errors.cc_owner ? "border-rose-400" : "border-border"}`}
                      value={form.cc_owner}
                      onChange={(e) => setField("cc_owner", e.target.value.toUpperCase())}
                    />
                    <label className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-sm peer-placeholder-shown:text-[#9bb0c9] peer-focus:top-3 peer-focus:text-xs peer-focus:text-[#1f5fb6] peer-[:not(:placeholder-shown)]:top-3 peer-[:not(:placeholder-shown)]:text-xs">
                      Kart Sahibi
                    </label>
                  </div>
                  {errors.cc_owner ? <p className="mt-1 text-[11px] text-rose-500">{errors.cc_owner}</p> : null}
                  </div>
                  <div className="md:col-span-2">
                  <div className="relative">
                    <input
                      onFocus={() => setCardFace("front")}
                      placeholder=" "
                      className={`peer h-11 w-full rounded-lg border px-3 pt-4 text-sm tracking-[0.08em] ${errors.card_number ? "border-rose-400" : "border-border"}`}
                      value={formatCardForDisplay(form.card_number)}
                      onChange={(e) => setField("card_number", normalizeCardNo(e.target.value))}
                    />
                    <label className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-sm peer-placeholder-shown:text-[#9bb0c9] peer-focus:top-3 peer-focus:text-xs peer-focus:text-[#1f5fb6] peer-[:not(:placeholder-shown)]:top-3 peer-[:not(:placeholder-shown)]:text-xs">
                      Kart Numarası
                    </label>
                  </div>
                  {errors.card_number ? <p className="mt-1 text-[11px] text-rose-500">{errors.card_number}</p> : null}
                  </div>
                  <div>
                    <div className="relative">
                      <input
                        onFocus={() => setCardFace("front")}
                        placeholder=" "
                        className={`peer h-11 w-full rounded-lg border px-3 pt-4 text-sm ${errors.expiry_month || errors.expiry_year ? "border-rose-400" : "border-border"}`}
                        value={formatExpiryDisplay(form.expiry_month, form.expiry_year)}
                        onChange={(e) => {
                          const raw = String(e.target.value || "").replace(/[^\d]/g, "").slice(0, 4);
                          const m = normalizeExpiryMonth(raw.slice(0, 2));
                          const y = raw.slice(2, 4);
                          setField("expiry_month", m);
                          setField("expiry_year", y);
                        }}
                      />
                      <label className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-sm peer-placeholder-shown:text-[#9bb0c9] peer-focus:top-3 peer-focus:text-xs peer-focus:text-[#1f5fb6] peer-[:not(:placeholder-shown)]:top-3 peer-[:not(:placeholder-shown)]:text-xs">
                        Son Kullanım (AA / YY)
                      </label>
                    </div>
                    {(errors.expiry_month || errors.expiry_year) ? <p className="mt-1 text-[11px] text-rose-500">{errors.expiry_month || errors.expiry_year}</p> : null}
                  </div>
                  <div>
                    <div className="relative">
                    <input
                      onFocus={() => setCardFace("back")}
                      onBlur={() => setCardFace("front")}
                      maxLength={3}
                      placeholder=" "
                      className={`peer h-11 w-full rounded-lg border px-3 pt-4 text-sm ${errors.cvv ? "border-rose-400" : "border-border"}`}
                      value={form.cvv}
                      onChange={(e) => setField("cvv", String(e.target.value || "").replace(/[^\d]/g, "").slice(0, 3))}
                    />
                    <label className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-sm peer-placeholder-shown:text-[#9bb0c9] peer-focus:top-3 peer-focus:text-xs peer-focus:text-[#1f5fb6] peer-[:not(:placeholder-shown)]:top-3 peer-[:not(:placeholder-shown)]:text-xs">
                      CVV
                    </label>
                    </div>
                    {errors.cvv ? <p className="mt-1 text-[11px] text-rose-500">{errors.cvv}</p> : null}
                  </div>
                  <div className="col-span-2">
                    <label className="inline-flex items-start gap-2 text-xs text-muted-foreground">
                      <input
                        type="checkbox"
                        checked={saveCardForRenewal}
                        onChange={(e) => setSaveCardForRenewal(e.target.checked)}
                      />
                      Kartı güvenli token olarak kaydet. Kart numarası/CVV saklanmaz, sadece maskeli kart ve ödeme tokenı saklanır.
                    </label>
                  </div>
                </div>
              </div>
            </article>
              </>
            ) : null}
          </div>

          <aside className="order-1 space-y-4 lg:order-2">
            <article className="rounded-2xl border border-border bg-card p-4 shadow-sm">
              <label className="mb-2 block text-xs font-medium text-[#5e7ea4]">Paket Seçimi</label>
              <div className="mb-3 grid gap-2 md:grid-cols-3 lg:grid-cols-1">
                {packageCards.map((pkg) => {
                  const key = String(pkg?.key || "").toLowerCase();
                  const isActive = key === activePlanKey;
                  const pMonthly = getPlanCyclePrice(pkg, "monthly", 0);
                  const pYearly = getPlanCyclePrice(pkg, "yearly", pMonthly * 12);
                  const save = Math.max(0, (pMonthly * 12) - pYearly);
                  const limitLabel = getIntegrationLimitLabel(pkg);
                  return (
                    <button
                      key={key || pkg?.name}
                      type="button"
                      onClick={() => changePlan(key)}
                      className={`rounded-xl border p-3 text-left transition-all ${
                        isActive
                          ? "border-[#2d74c8] bg-[linear-gradient(180deg,#ffffff_0%,#eff6ff_100%)] shadow-[0_10px_22px_rgba(31,95,182,0.2)]"
                          : "border-border bg-card hover:border-[#b8d4f5] hover:shadow-[0_8px_18px_rgba(31,95,182,0.1)]"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-sm font-semibold ${isActive ? "text-[#174d98]" : "text-foreground"}`}>{pkg?.name || pkg?.key}</span>
                        {save > 0 ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Yıllık {formatTry(save)} avantaj</span> : null}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {formatTry(pMonthly)} / ay · {formatTry(pYearly)} / yıl
                      </div>
                      <div className="mt-1 text-xs font-medium text-[#3f6fa8]">{limitLabel}</div>
                    </button>
                  );
                })}
              </div>
              <label className="mb-2 block text-xs font-medium text-[#5e7ea4]">Abonelik Tercihi</label>
              <div className="grid w-full grid-cols-2 gap-2 rounded-2xl border border-[#d7e5f7] bg-[linear-gradient(180deg,#f8fbff_0%,#eef5ff_100%)] p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_8px_22px_rgba(27,74,136,0.08)]">
                <button
                  type="button"
                  onClick={() => changeBillingCycle("monthly")}
                  className={`h-14 rounded-xl border px-3 text-left text-xs font-semibold transition-all duration-200 ${
                    !selectedIsYearly
                      ? "border-[#2d74c8] bg-[linear-gradient(180deg,#ffffff_0%,#eff6ff_100%)] text-[#1f5fb6] shadow-[0_10px_22px_rgba(31,95,182,0.24)]"
                      : "border-transparent text-muted-foreground hover:bg-white/70"
                  }`}
                >
                  <div className={!selectedIsYearly ? "text-[#194f99]" : ""}>Aylık Abonelik</div>
                  <div className={`text-[11px] ${!selectedIsYearly ? "text-[#3f6fa8]" : "opacity-80"}`}>{formatTry(monthlyBase)} / ay</div>
                </button>
                <button
                  type="button"
                  onClick={() => changeBillingCycle("yearly")}
                  className={`h-14 rounded-xl border px-3 text-left text-xs font-semibold transition-all duration-200 ${
                    selectedIsYearly
                      ? "border-[#3f9c88] bg-[linear-gradient(180deg,#ffffff_0%,#ecfbf5_100%)] text-[#0f7a5c] shadow-[0_10px_22px_rgba(18,155,118,0.24)]"
                      : "border-transparent text-muted-foreground hover:bg-white/70"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span>Yıllık Abonelik</span>
                    {yearlyAdvantage > 0 ? <span className={`rounded-full px-2 py-0.5 text-[10px] ${selectedIsYearly ? "bg-emerald-100 text-emerald-700" : "bg-emerald-100 text-emerald-700"}`}>Avantajlı</span> : null}
                  </div>
                  <div className={`text-[11px] ${selectedIsYearly ? "text-emerald-700" : "opacity-80"}`}>{formatTry(yearlyBase)} / yıl</div>
                </button>
              </div>
              {yearlyAdvantage > 0 ? (
                <p className="mt-1 text-[11px] text-emerald-700">Yıllık seçimde {formatTry(yearlyAdvantage)} avantaj.</p>
              ) : null}
            </article>
            <article className="sticky top-20 hidden overflow-hidden rounded-2xl border border-border bg-card shadow-[0_16px_45px_rgba(24,61,110,0.14)] lg:block">
              <div className="bg-[linear-gradient(120deg,#163f78_0%,#1e5aa1_40%,#0f8aa1_100%)] p-5 text-white">
                <div className="text-xs uppercase tracking-[0.12em] text-blue-100">Sipariş Özeti</div>
                <div className="mt-1 text-lg font-semibold">{selectedPlan?.name || selectedIntent?.plan_key || "Paket seçilmedi"}</div>
                <div className="mt-1 text-sm text-blue-100">{selectedPeriodLabel}</div>
              </div>
              <div className="space-y-4 p-5">
                {!selectedIntent ? (
                  <div className="text-sm text-muted-foreground">Ödeme bekleyen kayıt yok.</div>
                ) : (
                  <>
                    <div className="rounded-xl border border-[#d9eafc] bg-muted/30 p-3">
                      <div className="mb-2 rounded-lg border border-border bg-card p-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Paket</span>
                          <span className="font-semibold text-foreground">{selectedPlan?.name || selectedIntent.plan_key}</span>
                        </div>
                        <div className="mt-1 flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Sağlayıcı</span>
                          <span className="font-semibold text-foreground">{providerSummary}</span>
                        </div>
                        <div className="mt-1 flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Periyot</span>
                          <span className="font-semibold text-foreground">{selectedPeriodLabel}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Paket Tutarı</span>
                        <span className="font-semibold text-foreground">{formatTry(baseAmount)}</span>
                      </div>
                      {discountAmount > 0 ? (
                        <div className="mt-2 flex items-center justify-between text-sm">
                          <span className="text-emerald-700">Promosyon ({appliedPromo})</span>
                          <span className="font-semibold text-emerald-700">- {formatTry(discountAmount)}</span>
                        </div>
                      ) : null}
                      <div className="mt-2 flex items-center justify-between border-t border-[#e3eefb] pt-2 text-sm">
                        <span className="font-semibold text-foreground">Toplam</span>
                        <span className="text-base font-semibold text-foreground">{formatTry(totalAmount)}</span>
                      </div>
                    </div>

                    <div className="rounded-xl border border-border bg-muted/30 p-3">
                      <div className="flex gap-2">
                        <div className="relative w-full">
                          <input
                            className="peer h-10 w-full rounded-lg border border-border bg-card px-3 pt-4 text-sm"
                            placeholder=" "
                            value={promoInput}
                            onChange={(e) => setPromoInput(String(e.target.value || "").toUpperCase())}
                          />
                          <label className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-sm peer-placeholder-shown:text-[#9bb0c9] peer-focus:top-3 peer-focus:text-xs peer-focus:text-[#1f5fb6] peer-[:not(:placeholder-shown)]:top-3 peer-[:not(:placeholder-shown)]:text-xs">
                            Promosyon Kodu
                          </label>
                        </div>
                        <button
                          type="button"
                          disabled={!String(promoInput || "").trim()}
                          onClick={applyPromo}
                          className="h-10 rounded-lg bg-[#1f5fb6] px-3 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-60"
                        >
                          Uygula
                        </button>
                      </div>
                    </div>

                    <label className="inline-flex items-start gap-2 text-xs text-muted-foreground">
                      <input type="checkbox" checked={form.terms_approved} onChange={(e) => setField("terms_approved", e.target.checked)} />
                      Gizlilik ve Kullanıcı Sözleşmesini onaylıyorum.
                    </label>
                    {errors.terms_approved ? <p className="text-[11px] text-rose-500">{errors.terms_approved}</p> : null}

                    <Button disabled={saving} onClick={continuePayment} className="h-11 w-full rounded-xl bg-[#1487a3] hover:bg-[#12779b] text-white hover:opacity-95">
                      {saving ? "PayTR'ye Yönlendiriliyor..." : "Ödemeyi Tamamla"}
                    </Button>
                    <button type="button" onClick={() => navigate("/panel/entegrasyonlar")} className="h-10 w-full rounded-xl border border-border text-sm font-medium text-foreground hover:bg-muted/30">
                      Entegrasyonlara Dön
                    </button>
                    <div className="inline-flex items-center gap-2 text-[11px] text-[#6f8dab]">
                      <ShieldCheck className="h-3.5 w-3.5" />
                      Kart bilgileri PayTR güvenli ödeme altyapısı ile işlenir.
                    </div>
                  </>
                )}
              </div>
            </article>

            
          </aside>

          <article className="order-3 overflow-hidden rounded-2xl border border-border bg-card shadow-[0_16px_45px_rgba(24,61,110,0.14)] lg:hidden">
            <div className="bg-[linear-gradient(120deg,#163f78_0%,#1e5aa1_40%,#0f8aa1_100%)] p-5 text-white">
              <div className="text-xs uppercase tracking-[0.12em] text-blue-100">Sipariş Özeti</div>
              <div className="mt-1 text-lg font-semibold">{selectedPlan?.name || selectedIntent?.plan_key || "Paket seçilmedi"}</div>
              <div className="mt-1 text-sm text-blue-100">{selectedPeriodLabel}</div>
            </div>
            <div className="space-y-4 p-5">
              {!selectedIntent ? (
                <div className="text-sm text-muted-foreground">Ödeme bekleyen kayıt yok.</div>
              ) : (
                <>
                  <div className="rounded-xl border border-[#d9eafc] bg-muted/30 p-3">
                    <div className="mb-2 rounded-lg border border-border bg-card p-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Paket</span>
                        <span className="font-semibold text-foreground">{selectedPlan?.name || selectedIntent.plan_key}</span>
                      </div>
                      <div className="mt-1 flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Sağlayıcı</span>
                        <span className="font-semibold text-foreground">{providerSummary}</span>
                      </div>
                      <div className="mt-1 flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Periyot</span>
                        <span className="font-semibold text-foreground">{selectedPeriodLabel}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Paket Tutarı</span>
                      <span className="font-semibold text-foreground">{formatTry(baseAmount)}</span>
                    </div>
                    {discountAmount > 0 ? (
                      <div className="mt-2 flex items-center justify-between text-sm">
                        <span className="text-emerald-700">Promosyon ({appliedPromo})</span>
                        <span className="font-semibold text-emerald-700">- {formatTry(discountAmount)}</span>
                      </div>
                    ) : null}
                    <div className="mt-2 flex items-center justify-between border-t border-[#e3eefb] pt-2 text-sm">
                      <span className="font-semibold text-foreground">Toplam</span>
                      <span className="text-base font-semibold text-foreground">{formatTry(totalAmount)}</span>
                    </div>
                  </div>

                  <div className="rounded-xl border border-border bg-muted/30 p-3">
                    <div className="flex gap-2">
                      <div className="relative w-full">
                        <input
                          className="peer h-10 w-full rounded-lg border border-border bg-card px-3 pt-4 text-sm"
                          placeholder=" "
                          value={promoInput}
                          onChange={(e) => setPromoInput(String(e.target.value || "").toUpperCase())}
                        />
                        <label className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-sm peer-placeholder-shown:text-[#9bb0c9] peer-focus:top-3 peer-focus:text-xs peer-focus:text-[#1f5fb6] peer-[:not(:placeholder-shown)]:top-3 peer-[:not(:placeholder-shown)]:text-xs">
                          Promosyon Kodu
                        </label>
                      </div>
                      <button
                        type="button"
                        disabled={!String(promoInput || "").trim()}
                        onClick={applyPromo}
                        className="h-10 rounded-lg bg-[#1f5fb6] px-3 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-60"
                      >
                        Uygula
                      </button>
                    </div>
                  </div>

                  <label className="inline-flex items-start gap-2 text-xs text-muted-foreground">
                    <input type="checkbox" checked={form.terms_approved} onChange={(e) => setField("terms_approved", e.target.checked)} />
                    Gizlilik ve Kullanıcı Sözleşmesini onaylıyorum.
                  </label>
                  {errors.terms_approved ? <p className="text-[11px] text-rose-500">{errors.terms_approved}</p> : null}

                  <Button disabled={saving} onClick={continuePayment} className="h-11 w-full rounded-xl bg-[#1487a3] hover:bg-[#12779b] text-white hover:opacity-95">
                    {saving ? "PayTR'ye Yönlendiriliyor..." : "Ödemeyi Tamamla"}
                  </Button>
                  <button type="button" onClick={() => navigate("/panel/entegrasyonlar")} className="h-10 w-full rounded-xl border border-border text-sm font-medium text-foreground hover:bg-muted/30">
                    Entegrasyonlara Dön
                  </button>
                  <div className="inline-flex items-center gap-2 text-[11px] text-[#6f8dab]">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Kart bilgileri PayTR güvenli ödeme altyapısı ile işlenir.
                  </div>
                </>
              )}
            </div>
          </article>
        </section>
      </div>
    </div>
  );
}
